/**
 * OrcamentoChat.jsx
 *
 * Componente de chat para criação de orçamentos com assistencia de IA.
 * Segue um fluxo conversacional onde o usuario interage com o bot para:
 * - Colher informacoes do cliente (nome, telefone, email, endereco)
 * - Adicionar produtos e acessorios ao orçamento
 * - Finalizar e salvar o orçamento
 *
 * Integracao com API:
 * - POST /api/chat - Chat principal com streaming de resposta
 * - POST /api/rag-search - Busca de produtos via RAG
 * - POST /api/save-orcamento - Salvamento final do orçamento
 *
 * Padroes herdados de OrcamentoV2.jsx
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';
import { clienteService } from '../services/clienteService';

// =============================================================================
// CONFIGURACOES E CONSTANTES
// =============================================================================

// URL base da API (definida como constante para facil manutencao)
const API_BASE_URL = 'https://saas-ads-rafa.comercial.ws/api';

/**
 * Mensagem inicial do bot quando o chat comeca
 * Guia o usuario sobre como usar o sistema
 */
const MENSAGEM_BOAS_VINDAS = `Olá! Sou seu assistente para criar orçamentos 😊

Posso te ajudar a:
• Coletar informações do cliente
• Buscar e adicionar produtos ao orçamento
• Finalizar e salvar o orçamento

Digite "iniciar orçamento" para começar ou me descreva o que precisa!

(v2026-09-10 — fluxo "mais barato" automático)`;

/**
 * Tool calls suportados pelo bot
 * Define as acoes que o bot pode executar durante a conversa
 */
const TOOL_DEFINITIONS = [
  {
    name: 'buscar_produto',
    description: 'Busca produtos no catalogo usando pesquisa semantica RAG',
    input_schema: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Termo de busca do produto (nome, categoria, tecido, etc)'
        }
      },
      required: ['query']
    }
  },
  {
    name: 'adicionar_produto',
    description: 'Adiciona um produto ao orçamento com suas dimensoes e quantidade',
    input_schema: {
      type: 'object',
      properties: {
        produto_id: { type: 'string', description: 'ID do produto' },
        nome: { type: 'string', description: 'Nome do produto' },
        largura: { type: 'number', description: 'Largura em metros' },
        altura: { type: 'number', description: 'Altura em metros' },
        quantidade: { type: 'number', description: 'Quantidade de peças' },
        preco_unitario: { type: 'number', description: 'Preço unitário' },
        metodo_calculo: { type: 'string', description: 'm2, ml, ou altura' }
      },
      required: ['produto_id', 'nome', 'largura', 'altura', 'quantidade']
    }
  },
  {
    name: 'adicionar_acessorio',
    description: 'Adiciona um acessório ao orçamento',
    input_schema: {
      type: 'object',
      properties: {
        acessorio_id: { type: 'string', description: 'ID do acessório' },
        nome: { type: 'string', description: 'Nome do acessório' },
        quantidade: { type: 'number', description: 'Quantidade' },
        preco_unitario: { type: 'number', description: 'Preço unitário' },
        unidade: { type: 'string', description: 'Unidade de medida (m, un, etc)' }
      },
      required: ['acessorio_id', 'nome', 'quantidade']
    }
  },
  {
    name: 'atualizar_cliente',
    description: 'Atualiza ou define as informações do cliente do orçamento',
    input_schema: {
      type: 'object',
      properties: {
        nome: { type: 'string', description: 'Nome completo do cliente' },
        telefone: { type: 'string', description: 'Telefone com DDD' },
        email: { type: 'string', description: 'E-mail do cliente' },
        endereco: { type: 'string', description: 'Endereço completo' }
      },
      required: ['nome']
    }
  },
  {
    name: 'remover_item',
    description: 'Remove um item do orçamento pelo ID',
    input_schema: {
      type: 'object',
      properties: {
        item_id: { type: 'string', description: 'ID do item a remover' }
      },
      required: ['item_id']
    }
  },
  {
    name: 'listar_itens',
    description: 'Lista todos os itens atuais do orçamento com totais',
    input_schema: { type: 'object', properties: {} }
  },
  {
    name: 'finalizar_orcamento',
    description: 'Finaliza o orçamento e salva no banco de dados',
    input_schema: { type: 'object', properties: {} }
  }
];

// =============================================================================
// UTILIDADES DE FORMATACAO
// =============================================================================

/**
 * Formata um valor monetario para exibicao em Real Brasileiro
 * @param {number} value - Valor a ser formatado
 * @returns {string} Valor formatado (ex: "1.234,56")
 */
function fmt(value) {
  return (value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/**
 * Calcula o subtotal de um produto baseado em suas dimensoes
 * Suporta metodos de calculo: m2 (area), ml (metro linear), altura
 *
 * @param {Object} produto - Produto com preco_venda e metodo_calculo
 * @param {number} largura - Largura em metros
 * @param {number} altura - Altura em metros
 * @param {number} quantidade - Quantidade de pecas
 * @returns {number} Subtotal calculado
 */
function calcSubtotal(produto, largura, altura, quantidade) {
  if (!produto || !largura || !altura) return 0;

  const l = parseFloat(largura);
  const a = parseFloat(altura);
  const q = parseInt(quantidade) || 1;
  const preco = parseFloat(produto.preco_venda) || 0;
  const metodo = (produto.metodo_calculo || 'm2').toLowerCase();
  const areaMinima = parseFloat(produto.area_minima) || 0;

  let base = 0;

  if (metodo === 'ml' || metodo === 'linear') {
    // Metro linear: largura x preco
    base = l * preco;
  } else if (metodo === 'altura') {
    // Por altura: altura x preco
    base = a * preco;
  } else {
    // Metro quadrado padrao: area com minimo
    const area = l * a;
    base = Math.max(area, areaMinima) * preco;
  }

  return base * q;
}

/**
 * Calcula o valor do bandô (largura × bando_venda).
 * Fórmula fixa conforme configuração: largura * valor_venda
 *
 * @param {number} largura - Largura em metros
 * @param {number} bandoVenda - Preço de venda do bandô por metro (de configuracoes.bando_venda)
 * @returns {number} Valor do bandô
 */
function calcBando(largura, bandoVenda) {
  const l = parseFloat(largura) || 0;
  const v = parseFloat(bandoVenda) || 0;
  return l * v;
}

// =============================================================================
// COMPONENTE: MessageBubble
// Exibe uma bolha de mensagem no chat (usuario ou bot)
// =============================================================================

/**
 * Componente inline para renderizar bolhas de mensagem
 * Diferencia visualmente mensagens de usuario, bot e erros
 * Tambem renderiza cards de produtos encontrados
 *
 * @param {Object} props
 * @param {Object} props.message - Objeto da mensagem {role, content, items, timestamp, isError, toolResult}
 * @param {Function} props.onEditItem - Callback para editar item
 * @param {Function} props.onDeleteItem - Callback para remover item
 */
function MessageBubble({ message, onEditItem, onDeleteItem }) {
  const isUser = message.role === 'user';
  const isError = message.isError;
  const isToolResult = !!message.toolResult;

  // Estilos base para bolhas
  const bubbleStyle = {
    maxWidth: '75%',
    padding: '12px 16px',
    borderRadius: '16px',
    wordBreak: 'break-word',
    lineHeight: 1.5,
    fontSize: 14,
    whiteSpace: 'pre-wrap',
  };

  // Estilos especificos por tipo de mensagem
  const userBubbleStyle = {
    ...bubbleStyle,
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    borderBottomRightRadius: '4px',
    marginLeft: 'auto',
  };

  const botBubbleStyle = {
    ...bubbleStyle,
    background: isError
      ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
      : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
    color: isError ? '#991b1b' : '#1f2937',
    borderBottomLeftRadius: '4px',
    marginRight: 'auto',
  };

  // Container do card de produto encontrado
  const productCardStyle = {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  // Renderiza o conteudo da mensagem
  const renderContent = () => {
    if (isToolResult && message.toolResult) {
      // Resultado de tool call - exibe em formato estruturado
      return (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8, color: '#374151' }}>
            {message.toolResult.title || 'Resultado'}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>

          {/* Renderiza cards de produtos se houver */}
          {message.toolResult.products && message.toolResult.products.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
                Produtos encontrados:
              </div>
              {message.toolResult.products.map((prod, idx) => (
                <div key={prod.id || idx} style={productCardStyle}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{prod.nome}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Código: {prod.codigo || prod.id}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#059669', marginTop: 4 }}>
                    R$ {fmt(prod.preco_venda)}/{prod.metodo_calculo === 'ml' ? 'm' : 'm²'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return message.content;
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: 12,
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div style={isUser ? userBubbleStyle : botBubbleStyle}>
        {renderContent()}

        {/* Timestamp opcional */}
        {message.timestamp && (
          <div style={{
            fontSize: 10,
            opacity: 0.7,
            marginTop: 4,
            textAlign: isUser ? 'right' : 'left',
          }}>
            {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENTE: ProductItemCard
// Card editavel para itens do orçamento exibidos no chat
// =============================================================================

/**
 * Card compacto para exibir e editar/remover itens do orçamento
 * Exibido inline no chat quando o bot adiciona um produto
 *
 * @param {Object} props
 * @param {Object} props.item - Item do orçamento
 * @param {Function} props.onEdit - Callback para editar
 * @param {Function} props.onDelete - Callback para remover
 */
function ProductItemCard({ item, onEdit, onDelete }) {
  const isAccessory = item.tipo === 'acessorio';
  const [isEditing, setIsEditing] = React.useState(false);
  const [editLargura, setEditLargura] = React.useState(item.selection?.largura || item.largura || 1);
  const [editAltura, setEditAltura] = React.useState(item.selection?.altura || item.altura || 1);
  const [editQuantidade, setEditQuantidade] = React.useState(item.selection?.quantidade || item.quantidade || 1);
  const [showInstalacaoBox, setShowInstalacaoBox] = React.useState(false);
  const [instalacaoBox, setInstalacaoBox] = React.useState('');

  // Calcular subtotal em tempo real durante edição
  const calcSubtotalLocal = (l, a, q) => {
    const preco = parseFloat(item.produto?.preco_venda || item.unit_price || 0) || 0;
    const metodo = (item.produto?.metodo_calculo || 'm2').toLowerCase();
    const areaMinima = parseFloat(item.produto?.area_minima || 0) || 0;
    const area = (parseFloat(l) || 0) * (parseFloat(a) || 0);
    const qtd = parseInt(q) || 1;
    let base;
    if (metodo === 'ml' || metodo === 'linear') {
      base = (parseFloat(l) || 0) * preco;
    } else {
      base = Math.max(area, areaMinima) * preco;
    }
    const subtotalProduto = base * qtd;
    const bandoValor = item.com_bando ? calcBando(l, item.bando_venda_usado) * qtd : 0;
    const instalacao = parseFloat(item.instalacao_valor) || 0;
    return subtotalProduto + bandoValor + instalacao;
  };

  const handleAdicionarInstalacao = () => {
    const v = parseFloat(instalacaoBox.replace(',', '.')) || 0;
    if (v <= 0) return;
    const novoSubtotal = (item.subtotal || 0) + v;
    onEdit?.(item.id, {
      instalacao_valor: v,
      subtotal: novoSubtotal,
    });
    setInstalacaoBox('');
    setShowInstalacaoBox(false);
  };

  const handleRemoverInstalacao = () => {
    const v = parseFloat(item.instalacao_valor) || 0;
    onEdit?.(item.id, {
      instalacao_valor: 0,
      subtotal: (item.subtotal || 0) - v,
    });
  };

  const cardStyle = {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const badgeStyle = {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 4,
    textTransform: 'uppercase',
  };

  const inputStyle = {
    width: 60,
    padding: '4px 6px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 12,
    marginRight: 4,
  };

  const handleSave = () => {
    const novoSubtotal = calcSubtotalLocal(editLargura, editAltura, editQuantidade);
    onEdit?.(item.id, {
      selection: {
        largura: parseFloat(editLargura) || 1,
        altura: parseFloat(editAltura) || 1,
        quantidade: parseInt(editQuantidade) || 1
      },
      subtotal: novoSubtotal
    });
    setIsEditing(false);
  };

  return (
    <div style={cardStyle}>
      <div style={{ flex: 1 }}>
        {/* Badge de tipo e nome */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{
            ...badgeStyle,
            background: isAccessory ? '#dbeafe' : '#dcfce7',
            color: isAccessory ? '#1e40af' : '#15803d',
          }}>
            {isAccessory ? 'ACC' : 'PROD'}
          </span>
          <span style={{ fontWeight: 600, color: '#111827' }}>
            {item.produto?.nome || item.nome}
          </span>
        </div>

        {/* Detalhes do item - modo edição ou visualização */}
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          {isAccessory ? (
            isEditing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                <label>Qtd:</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={editQuantidade}
                  onChange={e => setEditQuantidade(e.target.value)}
                  style={inputStyle}
                />
              </div>
            ) : (
              <>
                <span>Qtd: {item.quantidade} {item.unit || 'un'}</span>
                {item.color && <span> · Cor: {item.color}</span>}
              </>
            )
          ) : (
            isEditing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                <input
                  type="number"
                  step="0.1"
                  value={editLargura}
                  onChange={e => setEditLargura(e.target.value)}
                  style={inputStyle}
                  placeholder="L"
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  value={editAltura}
                  onChange={e => setEditAltura(e.target.value)}
                  style={inputStyle}
                  placeholder="A"
                />
                <span style={{ marginLeft: 6 }}>Qtd:</span>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={editQuantidade}
                  onChange={e => setEditQuantidade(e.target.value)}
                  style={inputStyle}
                />
              </div>
            ) : (
              <>
                <span>{item.selection?.largura || item.largura}m × {item.selection?.altura || item.altura}m</span>
                <span> · Qtd: {item.selection?.quantidade || item.quantidade}</span>
              </>
            )
          )}
        </div>
      </div>

      {/* Preco e acoes */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 700, color: '#059669', fontSize: 14 }}>
          R$ {fmt(isEditing ? calcSubtotalLocal(editLargura, editAltura, editQuantidade) : item.subtotal)}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                style={{
                  fontSize: 11,
                  padding: '4px 8px',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Salvar
              </button>
              <button
                onClick={() => setIsEditing(false)}
                style={{
                  fontSize: 11,
                  padding: '4px 8px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  fontSize: 11,
                  padding: '4px 8px',
                  background: '#fef3c7',
                  color: '#92400e',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Editar
              </button>
              <button
                onClick={() => onDelete?.(item.id)}
                style={{
                  fontSize: 11,
                  padding: '4px 8px',
                  background: '#fee2e2',
                  color: '#991b1b',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Remover
              </button>
            </>
          )}
        </div>
      </div>

      {/* Linha do bandô (se houver) */}
      {item.com_bando && parseFloat(item.bando_valor) > 0 && (
        <div style={{ flexBasis: '100%', fontSize: 11, color: '#6b7280', marginTop: 4 }}>
          + Bandô: R$ {fmt(item.bando_valor)}
        </div>
      )}

      {/* Linha da instalação (se houver) */}
      {parseFloat(item.instalacao_valor) > 0 && (
        <div style={{ flexBasis: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            + Instalação: R$ {fmt(item.instalacao_valor)}
          </span>
          <button
            onClick={handleRemoverInstalacao}
            style={{
              fontSize: 10,
              padding: '2px 6px',
              background: 'transparent',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            remover
          </button>
        </div>
      )}

      {/* Box digitável de instalação */}
      {!isAccessory && (
        <div style={{ flexBasis: '100%', marginTop: 6 }}>
          {showInstalacaoBox ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Valor da instalação (R$)"
                value={instalacaoBox}
                onChange={e => setInstalacaoBox(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAdicionarInstalacao(); }}
                autoFocus
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  fontSize: 12,
                }}
              />
              <button
                onClick={handleAdicionarInstalacao}
                disabled={!parseFloat(instalacaoBox.replace(',', '.')) > 0}
                style={{
                  fontSize: 11,
                  padding: '6px 10px',
                  background: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Adicionar
              </button>
              <button
                onClick={() => { setShowInstalacaoBox(false); setInstalacaoBox(''); }}
                style={{
                  fontSize: 11,
                  padding: '6px 10px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowInstalacaoBox(true)}
              style={{
                fontSize: 11,
                padding: '4px 8px',
                background: '#eff6ff',
                color: '#1e40af',
                border: '1px dashed #93c5fd',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              + Adicionar instalação
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL: OrcamentoChat
// =============================================================================

/**
 * OrcamentoChat - Chat conversacional para criacao de orçamentos
 *
 * Fluxo principal:
 * 1. Boas-vindas inicial
 * 2. Usuario inicia orçamento -> coleta info do cliente
 * 3. Buscas e adicao de produtos via chat
 * 4. Revisao dos itens
 * 5. Finalizacao e salvamento
 *
 * Integra com API de chat (Gemma) e RAG para busca de produtos
 */
function OrcamentoChat() {
  const navigate = useNavigate();

  // ===========================================================================
  // ESTADO DO COMPONENTE
  // ===========================================================================

  /**
   * Estado principal de mensagens do chat
   * Cada mensagem: {role, content, timestamp, isError, toolResult}
   */
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: MENSAGEM_BOAS_VINDAS,
      timestamp: Date.now()
    }
  ]);

  /**
   * Rascunho do orçamento em construcao
   * Contem info do cliente e lista de itens
   */
  const [draft, setDraft] = useState({
    cliente: {
      nome: '',
      telefone: '',
      email: '',
      endereco: ''
    },
    itens: [], // Array de itens: {id, tipo, produto, selection, quantity, subtotal, ...}
    com_bando: false // Flag para adicionar bandô junto com próximo produto
  });

  /**
   * Passo atual no fluxo do chat
   * Controla qual acao o bot espera do usuario
   */
  const [step, setStep] = useState('inicio'); // inicio | cliente | produto | finalizado

  /**
   * Flag de loading durante chamadas de API
   */
  const [loading, setLoading] = useState(false);

  /**
   * Input do usuario
   */
  const [inputValue, setInputValue] = useState('');

  /**
   * Historico de mensagens para enviar a API (inclui apenas role e content)
   */
  const [chatHistory, setChatHistory] = useState([
    { role: 'system', content: `Você é um assistente de vendas especializado em cortinas e persianas.
Seu nome é Assistente Persialux.

Você ajuda clientes a criar orçamentos de produtos para cortinas e persianas.
Siga sempre este fluxo:

1. **Início**: Apresente-se e pergunte como pode ajudar
2. **Cliente**: Se não tiver info do cliente, colete: nome (obrigatório), telefone, email, endereço
3. **Produtos**: Ajude a encontrar e adicionar produtos ao orçamento usando a ferramenta buscar_produto
4. **Revisão**: Use listar_itens para mostrar o resumo quando solicitado
5. **Finalizar**: Use finalizar_orcamento quando o cliente confirmar

Sempre seja simpático, profissional e objetivo.
Use as ferramentas disponíveis para buscar produtos, adicionar ao orçamento e finalizar.
Quando buscar produtos, descreva brevemente cada um encontrado.
Sempre confirme ações antes de executar (ex: "Confirma a adição do produto X?").

Produtos disponíveis: cortinas, persianas, rolos, roman, blackout, screens, vertical.` }
  ]);

  /**
   * Referencia para scroll automatico
   */
  const messagesEndRef = useRef(null);

  /**
   * Referencia para o input de texto
   */
  const inputRef = useRef(null);

  /**
   * Flag para indicador de digitacao
   */
  const [isTyping, setIsTyping] = useState(false);

  /**
   * Clientes carregados do banco (para sugestao de cliente existente)
   */
  const [clientes, setClientes] = useState([]);

  /**
   * Toggle do painel de rascunho
   */
  const [showDraftPanel, setShowDraftPanel] = useState(true);

  /**
   * Estado para armazenar produtos pendentes de seleção
   * Quando o bot mostra opções, usuário pode selecionar por número
   */
  const [produtosPendentes, setProdutosPendentes] = useState([]);

  /**
   * Estado para salvar dimensões e query original quando usuário seleciona produto
   * Evita perder as dimensões quando usuário digita só "5"
   */
  const [pendingSelection, setPendingSelection] = useState(null);

  /**
   * Estado para o último produto selecionado com dimensões (para confirmação)
   */
  const [ultimoProdutoConfirmar, setUltimoProdutoConfirmar] = useState(null);

  // ===========================================================================
  // ESTADOS DE PAGAMENTO / INSTALAÇÃO / DESCONTO
  // ===========================================================================
  const [paymentMethod, setPaymentMethod] = useState('pix'); // pix | cartao | dinheiro | boleto
  const [installments, setInstallments] = useState(1); // para cartao
  const [discountRate, setDiscountRate] = useState(0); // percentual para pix
  const [instalacaoValor, setInstalacaoValor] = useState(0); // valor da instalação

  /**
   * Erro_dimensao: quando o chat tenta adicionar um produto cuja largura
   * excede a largura_maxima do tubo/modelo. Usado para exibir aviso ao usuário.
   */
  const [erroDimensao, setErroDimensao] = useState(null);
  const [bandoVenda, setBandoVenda] = useState(120); // preço/m de bandô (configuracoes.bando_venda)

  // ===========================================================================
  // INTENT PARSER - Detecta intenções e atualiza draft LOCALMENTE
  // Isso evita loops de tool calls e mantém contexto rápido
  // ===========================================================================

  /**
   * Padrões para detectar intenções do usuário
   */
  const INTENT_PATTERNS = {
    produto: /(?:cortina|persiana|rolo|band[ôo]|roman|blackout|screen|vertical|sombra|mosquiteiro|tela)/i,
    dimensao: /(?:(\d+[,.]?\d*)\s*[xX×]\s*(\d+[,.]?\d*)|(\d+[,.]?\d*)\s*(?:metros?|m)\s*(?:largura|larg|l)|(?:largura|larg)\s*:?\s*(\d+[,.]?\d*)|(?:altura|alt)\s*:?\s*(\d+[,.]?\d*))/,
    quantidade: /(?:qtd|quantidade|de\s+(\d+)|(\d+)\s+(?:unidades?|un|peças?|pc))/i,
    cliente_nome: /(?:cliente|cliente\s+(?:é|eh|nome)|nome(?:\s+do\s+cliente)?(?:\s+é|\s+eh)?)\s*:?\s*([A-Za-zÀ-ÖØ-öø-ÿ\s]{2,})/i,
    telefone: /(?:tel|fone|celular|whatsapp)\s*:?\s*(\d{10,11}|\(\d{2}\)\s*\d{4,5}[-\s]?\d{4})/i,
    finalizar: /(?:finaliz[aeo]r?|salvar|fechar|encerrar|grav[aeo]r?|confirma[r]?|pronto|pode\s+(?:salvar|fechar)|est[áa]\s+(?:bom|ok|certo))/i,
    novo: /(?:novo|iniciar|come[çc]ar|resetar|criar)\s*(?:or[çc]amento|or[çc]ament|or[çc]amento\s+novo)/i,
    listar: /(?:listar|mostrar|ver)\s*(?:itens?|produtos?|or[çc]amento|resumo)/i,
  };

  /**
   * Extrai dimensões de um texto
   * @param {string} text - Texto para extrair
   * @returns {{largura: number|null, altura: number|null}}
   */
  const extractDimensions = (text) => {
    const result = { largura: null, altura: null };
    // Normaliza "2:40" → "2.40" (suporta vírgula, ponto e dois-pontos como separador decimal)
    const norm = (text || '').replace(/(\d):(\d)/g, '$1.$2');

    // Padrão "2x1,5" ou "2X1.5" ou "2:40x1:80"
    const dimMatch = norm.match(/(?:(\d+[,.:]?\d*)\s*[xX×]\s*(\d+[,.:]?\d*))/);
    if (dimMatch) {
      result.largura = parseFloat(dimMatch[1].replace(',', '.').replace(':', '.'));
      result.altura = parseFloat(dimMatch[2].replace(',', '.').replace(':', '.'));
    }

    // Padrão "largura 2" ou "2 metros largura" ou "2 de largura" ou "2:40 de largura"
    const larguraMatch = norm.match(/(?:largura|larg)\s*:?\s*(\d+[,.:]?\d*)|(\d+[,.:]?\d*)\s*(?:metros?|m)?\s*(?:de\s+)?(?:largura|larg)/i);
    if (larguraMatch && !result.largura) {
      result.largura = parseFloat((larguraMatch[1] || larguraMatch[2]).replace(',', '.').replace(':', '.'));
    }

    // Padrão "altura 1,5" ou "1,5 de altura" ou "1:80 de altura"
    const alturaMatch = norm.match(/(?:altura|alt)\s*:?\s*(\d+[,.:]?\d*)|(\d+[,.:]?\d*)\s*(?:metros?|m)?\s*(?:de\s+)?(?:altura|alt)/i);
    if (alturaMatch && !result.altura) {
      result.altura = parseFloat((alturaMatch[1] || alturaMatch[2]).replace(',', '.').replace(':', '.'));
    }

    return result;
  };

  /**
   * Extrai quantidade de um texto
   * @param {string} text - Texto para extrair
   * @returns {number}
   */
  const extractQuantity = (text) => {
    const match = text.match(/(?:qtd|quantidade|de\s+(\d+)|(\d+)\s+(?:unidades?|un|peças?|pc))/i);
    if (match) {
      return parseInt(match[1] || match[2] || '1');
    }
    return 1;
  };

  /**
   * Detecta e processa intenções localmente
   * Atualiza draft antes de enviar para a API
   *
   * @param {string} userMessage - Mensagem do usuário
   * @returns {{action: string|null, data: object, response: string|null}}
   */
  const processLocalIntent = useCallback(async (userMessage) => {
    const msg = userMessage.trim();
    console.log('[intent] msg:', msg, 'padrao produto:', INTENT_PATTERNS.produto.test(msg));

    // 0) SELEÇÃO DE PRODUTO POR NÚMERO
    // Ex: "1, 2x1,5" ou "2 - 1.5x2" ou "1"
    if (produtosPendentes.length > 0) {
      const numMatch = msg.match(/^(\d+)[\s,.\-]*(\d+[,.]?\d*)\s*[xX×]\s*(\d+[,.]?\d*)/);
      const numOnlyMatch = msg.match(/^(\d+)$/);
      const confirmMatch = /^(?:sim|s|ok|confirmar|add|adicionar|y|yes)$/i.test(msg);

      // Se confirmou "sim" e tem um produto pendente com dimensões salvas
      if (confirmMatch && ultimoProdutoConfirmar) {
        const produto = ultimoProdutoConfirmar;
        const { largura, altura } = produto.dims;
        setProdutosPendentes([]);
        setUltimoProdutoConfirmar(null);
        setPendingSelection(null);

        const preco = parseFloat(produto.preco_venda) || 0;
        const area = largura * altura;
        const metodo = (produto.metodo_calculo || 'm2').toLowerCase();
        const subtotalProduto = (metodo === 'ml' || metodo === 'linear')
          ? largura * preco
          : Math.max(area, parseFloat(produto.area_minima) || 0) * preco;

        const comBando = draft.com_bando;
        const bandoValor = comBando ? calcBando(largura, bandoVenda) : 0;
        const subtotal = subtotalProduto + bandoValor;

        const novoItem = {
          id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          tipo: 'produto',
          produto: {
            id: produto.id,
            nome: produto.nome || produto.name,
            preco_venda: preco,
            codigo: produto.codigo,
            metodo_calculo: produto.metodo_calculo || 'm2'
          },
          selection: { largura, altura, quantidade: 1 },
          subtotal,
          com_bando: comBando,
          bando_valor: bandoValor,
          bando_venda_usado: comBando ? bandoVenda : null
        };

        // Limpa flag de bandô do draft
        setDraft(prev => ({
          ...prev,
          itens: [...prev.itens, novoItem],
          com_bando: false
        }));

        const bandoStr = bandoValor > 0 ? `\n   + Bandô: R$ ${fmt(bandoValor)}` : '';
        const bandoMsg = comBando ? `\n✅ Bandô adicionado! (R$ ${fmt(bandoValor)})` : '';
        return {
          action: 'produto_adicionado',
          data: { item: novoItem },
          response: `✅ Adicionado: ${produto.nome}\nDimensões: ${largura}m × ${altura}m\nSubtotal: R$ ${fmt(subtotal)}${bandoStr}${bandoMsg || '\n'}\n\nPosso adicionar mais algum produto?`,
          cliente: clienteMerged
        };
      }

      // Se usuário digitou dimensões enquanto esperando confirmação
      if (ultimoProdutoConfirmar && !numMatch && !numOnlyMatch) {
        const dimsMatch = msg.match(/(\d+[,.]?\d*)\s*[xX×]\s*(\d+[,.]?\d*)/);
        if (dimsMatch) {
          const largura = parseFloat(dimsMatch[1].replace(',', '.'));
          const altura = parseFloat(dimsMatch[2].replace(',', '.'));
          const produto = { ...ultimoProdutoConfirmar };
          setProdutosPendentes([]);
          setUltimoProdutoConfirmar(null);
          setPendingSelection(null);

          const preco = parseFloat(produto.preco_venda) || 0;
          const area = largura * altura;
          const metodo = (produto.metodo_calculo || 'm2').toLowerCase();
          const subtotalProduto = (metodo === 'ml' || metodo === 'linear')
            ? largura * preco
            : Math.max(area, parseFloat(produto.area_minima) || 0) * preco;

          const comBandoLocal = prev?.com_bando || draft.com_bando;
          const bandoValor = comBandoLocal ? calcBando(largura, bandoVenda) : 0;
          const subtotal = subtotalProduto + bandoValor;

          const novoItem = {
            id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            tipo: 'produto',
            produto: {
              id: produto.id,
              nome: produto.nome || produto.name,
              preco_venda: preco,
              codigo: produto.codigo,
              metodo_calculo: produto.metodo_calculo || 'm2'
            },
            selection: { largura, altura, quantidade: 1 },
            subtotal,
            com_bando: comBandoLocal,
            bando_valor: bandoValor,
            bando_venda_usado: comBandoLocal ? bandoVenda : null
          };

          setDraft(prev => ({ ...prev, itens: [...prev.itens, novoItem], com_bando: false }));

          return {
            action: 'produto_adicionado',
            data: { item: novoItem },
            response: `✅ Adicionado: ${produto.nome}\nDimensões: ${largura}m × ${altura}m\nSubtotal: R$ ${fmt(subtotal)}\n\nPosso adicionar mais algum produto?`,
            cliente: clienteMerged
          };
        }
      }

      if (numMatch || numOnlyMatch) {
        const produtoIdx = parseInt(numOnlyMatch ? numOnlyMatch[1] : numMatch[1]) - 1;
        const produto = produtosPendentes[produtoIdx];

        if (produto) {
          // Se não veio com dimensões do número, usa as salvas ou padrão 1x1
          let largura = 1, altura = 1;
          if (numMatch) {
            largura = parseFloat(numMatch[2].replace(',', '.'));
            altura = parseFloat(numMatch[3].replace(',', '.'));
          } else if (pendingSelection?.dims && pendingSelection.dims.largura) {
            largura = pendingSelection.dims.largura;
            altura = pendingSelection.dims.altura;
          }

          setProdutosPendentes([]);

          // Se não tem dimensões, pede confirmação com medidas padrão ou extraídas
          if (!numMatch && (!pendingSelection?.dims || !pendingSelection.dims.largura)) {
            setUltimoProdutoConfirmar({ ...produto, dims: { largura, altura } });
            return {
              action: 'confirmar_produto',
              data: { produto, dims: { largura, altura } },
              response: `Confirma adicionar "${produto.nome}" (${produto.codigo}) com medidas ${largura}m × ${altura}m?\nResponda "sim" para confirmar ou digite as medidas (ex: 2x1,5)`,
              cliente: clienteMerged
            };
          }

          // Tem dimensões - adiciona direto
          const preco = parseFloat(produto.preco_venda) || 0;
          const area = largura * altura;
          const metodo = (produto.metodo_calculo || 'm2').toLowerCase();
          const subtotalProduto = (metodo === 'ml' || metodo === 'linear')
            ? largura * preco
            : Math.max(area, parseFloat(produto.area_minima) || 0) * preco;

          const bandoValor = comBandoLocal ? calcBando(largura, bandoVenda) : 0;
          const subtotal = subtotalProduto + bandoValor;

          const novoItem = {
            id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            tipo: 'produto',
            produto: {
              id: produto.id,
              nome: produto.nome || produto.name,
              preco_venda: preco,
              codigo: produto.codigo,
              metodo_calculo: produto.metodo_calculo || 'm2'
            },
            selection: { largura, altura, quantidade: 1 },
            subtotal,
            com_bando: comBandoLocal,
            bando_valor: bandoValor,
            bando_venda_usado: comBandoLocal ? bandoVenda : null
          };

          // Usa variável LOCAL comBandoLocal para evitar race condition com setDraft
          setDraft(prev => ({
            ...prev,
            itens: [...prev.itens, novoItem],
            com_bando: false // Limpa flag após usar
          }));
          setUltimoProdutoConfirmar(null);
          setPendingSelection(null);

          const bandoMsg = comBandoLocal ? '\n✅ Bandô adicionado!' : '';
          return {
            action: 'produto_adicionado',
            data: { item: novoItem },
            response: `✅ Adicionado: ${produto.nome}\nDimensões: ${largura}m × ${altura}m\nSubtotal: R$ ${fmt(subtotal)}${bandoMsg}\n\nPosso adicionar mais algum produto?`,
            cliente: clienteMerged
          };
        }
      }
    }

    // DETECTAR "BANDÔ" - já feito acima com comBandoLocal

    // 0) Calcular clienteMerged ANTES dos returns iniciais (necessário
    // para "novo", "listar" e "finalizar" que retornam clienteMerged)
    const clienteMerged = { ...draft.cliente };

    // 1) NOVO ORÇAMENTO
    if (INTENT_PATTERNS.novo.test(msg)) {
      return {
        action: 'novo',
        data: {},
        response: null, // Deixa o fluxo normal cuidar
        cliente: clienteMerged
      };
    }

    // 2) LISTAR ITENS
    if (INTENT_PATTERNS.listar.test(msg)) {
      if (draft.itens.length === 0) {
        return {
          action: 'listar',
          data: {},
          response: 'Seu orçamento ainda está vazio. Adicione produtos para eu listar aqui.',
          cliente: clienteMerged
        };
      }
      return {
        action: 'listar',
        data: { itens: draft.itens },
        response: null, // Deixa Gemma formatar a lista
        cliente: clienteMerged
      };
    }

    // 3) FINALIZAR
    if (INTENT_PATTERNS.finalizar.test(msg)) {
      if (!draft.cliente.nome) {
        return {
          action: 'finalizar',
          data: { error: 'precisa_cliente' },
          response: 'Antes de finalizar, preciso saber o nome do cliente. Qual o nome?',
          cliente: clienteMerged
        };
      }
      if (draft.itens.length === 0) {
        return {
          action: 'finalizar',
          data: { error: 'precisa_itens' },
          response: 'Adicione pelo menos um produto antes de finalizar.',
          cliente: clienteMerged
        };
      }
      return {
        action: 'finalizar',
        data: {},
        response: null,
        cliente: clienteMerged
      };
    }

    // 4) EXTRAIR DADOS DO CLIENTE (automático)
    // Ordem: telefone > endereço > nome (para não confundir)
    const clienteData = {};

    // Telefone BR: DDD(2) + 9 ou 8 dígitos + 4 dígitos
    const telMatch = msg.match(/(?<!\d)(?:\+?55[\s\-]?)?\(?(\d{2})\)?[\s\-]?9?\d{4}[\s\-]?\d{4}(?!\d)/);
    if (telMatch) {
      const digits = telMatch[0].replace(/\D/g, '');
      if (digits.length >= 10 && !draft.cliente.telefone) clienteData.telefone = digits;
    }

    // Endereço: aceita 1-5 palavras após o logradouro antes do número
    // "rua das flores 162" → "rua das flores 162"
    let endMatch = msg.match(/\b(?:alameda|rua|av\.?|avenida|travessa|r\.|rod\.)\s+(?:[A-Za-zÀ-Üà-ü][\wÀ-Üà-ü]*\s+){1,5}\d{1,5}\b/i);
    if (!endMatch) {
      // fallback: "Rua 162" sem nome
      endMatch = msg.match(/\b(?:alameda|rua|av|avenida|alameda|travessa|rodovia|estrada)\s+\d{1,5}\b/i);
    }
    if (endMatch && !draft.cliente.endereco) {
      clienteData.endereco = endMatch[0].trim();
    }

    // Nome: precisa limpar telefone + endereço + dimensões ANTES
    const msgClean = msg
      .replace(/(?<!\d)(?:\+?55[\s\-]?)?\(?(\d{2})\)?[\s\-]?9?\d{4}[\s\-]?\d{4}(?!\d)/g, '') // remove telefone
      .replace(/\b(?:alameda|rua|av\.?|avenida|travessa|r\.|rod\.)\s+(?:[A-Za-zÀ-Üà-ü][\wÀ-Üà-ü]*\s+){1,5}\d{1,5}\b/gi, '') // remove endereço completo
      .replace(/\b(?:alameda|rua|av|avenida|alameda|travessa|rodovia|estrada)\s+\d{1,5}\b/gi, '') // remove endereço sem nome
      .replace(/\d+[,.]?\d*\s*[xX×]\s*\d+[,.]?\d*/g, '') // remove dimensões
      .replace(/\d+[,.]?\d*\s*m(?:etros?)?/gi, '') // remove metros
      .replace(/\d+/g, '') // remove sobras numéricas
      .trim();

    const nomeMatch = msgClean.match(/(?:para\s+o|para\s+a|cliente|senhor[as]?)\s*:?\s*((?:[A-Za-zÀ-Üà-ü]{2,}(?:\s+[A-Za-zÀ-Üà-ü]{2,}){1,5}))/i)
      || msgClean.match(/^\s*((?:[A-Za-zÀ-Üà-ü]{2,}\s+){1,4}[A-Za-zÀ-Üà-ü]{2,})\b/);
    if (nomeMatch && !draft.cliente.nome) {
      // Filtra palavras que não são nome (artigos, preposições, logradouros, produtos)
      const palavrasBloqueadas = new Set([
        'para','com','sem','do','da','de','no','na','em','ao','à',
        'cortina','persiana','rolo','blackout','bandô','bando','tubo',
        'mais','barato','barata','menor','preco','preço','modelo',
        'tela','solar','persianas','telasolar','screen','solflex',
        'orcamento','orçamento','cliente','telefone','endereco','endereço',
        'rua','avenida','alameda','travessa','rodovia','estrada','av','r','rod',
        'wave','double','vision','new','shut','shutter','plissada','painel',
        'bambu','romana','vertical','horizontal',
      ]);
      const candidato = nomeMatch[1].split(' ').filter(w =>
        !palavrasBloqueadas.has(w.toLowerCase()) && w.length >= 2 && !w[0].match(/\d/)
      );
      if (candidato.length >= 2) {
        clienteData.nome = candidato.join(' ').split(' ').map(w =>
          w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
        ).join(' ');
      }
    }
    if (clienteData.nome || clienteData.telefone || clienteData.endereco) {
      setDraft(prev => ({ ...prev, cliente: { ...prev.cliente, ...clienteData } }));
    }

    // DETECTAR "BANDÔ" ANTES de detectar produto - usa variável LOCAL para evitar race condition
    const temBando = /\b(band[ôo]|bando)\b/i.test(msg);
    const comBandoLocal = temBando || draft.com_bando;
    if (temBando && !draft.com_bando) {
      // Marca bandô no draft para próximas mensagens
      setDraft(prev => ({ ...prev, com_bando: true }));
    }

    // 5) DETECTAR PRODUTO + DIMENSÕES → chama RAG v2 (LangGraph + FAISS)
    if (INTENT_PATTERNS.produto.test(msg)) {
      console.log('[OrcamentoChat] produto detectado, chamando /api/resolve:', msg.slice(0, 80));
      try {
        // Chama o microsserviço Python (:8001) que faz:
        //   ParserNode (regex) → SearchNode (FAISS + ILIKE) →
        //   ValidateNode (rerank) → FinalizeNode (monta payload)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);

        const response = await fetch(`${API_BASE_URL}/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: msg }),
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          console.warn(`[resolve] RAG v2 falhou (${response.status}), fallback para Gemma`);
          throw new Error(`RAG v2 status ${response.status}`);
        }

        const rag = await response.json();
        console.log('[OrcamentoChat] /api/resolve retornou:', {
          precisa_escolha: rag.item?.precisa_escolha,
          produto: rag.item?.produto?.nome?.slice(0, 50),
          opcoes: rag.item?.opcoes?.length || 0,
          subtotal: rag.item?.subtotal,
          estrategia: rag.estrategia_busca,
        });
        // Estrutura esperada:
        //   { cliente: {...}, item: { produto, dims, modelo, tecido, com_bando,
        //                            subtotal, precisa_escolha, opcoes, query },
        //     acessorios: [...], finalizar: bool, estrategia_busca: 'hybrid' }

        // Atualiza cliente (se nome/tel/endereço extraídos)
        const ragCliente = rag.cliente || {};
        const clienteUpdate = {};
        if (ragCliente.nome && !draft.cliente.nome) clienteUpdate.nome = ragCliente.nome;
        if (ragCliente.telefone && !draft.cliente.telefone) clienteUpdate.telefone = ragCliente.telefone;
        if (ragCliente.endereco && !draft.cliente.endereco) clienteUpdate.endereco = ragCliente.endereco;
        if (Object.keys(clienteUpdate).length > 0) {
          setDraft(prev => ({ ...prev, cliente: { ...prev.cliente, ...clienteUpdate } }));
        }

        const itemInfo = rag.item || {};
        const produto = itemInfo.produto;

        // Caso 1: precisa escolha do usuário (múltiplos candidatos)
        if (itemInfo.precisa_escolha && itemInfo.opcoes?.length > 0) {
          const opcoesTexto = itemInfo.opcoes
            .map((o, i) => `${i + 1}. ${o.nome} (${o.codigo || ''}) - R$ ${fmt(parseFloat(o.preco_venda) || 0)}/m²`)
            .join('\n');
          setProdutosPendentes(itemInfo.opcoes);
          setPendingSelection({
            dims: { largura: itemInfo.dims?.largura, altura: itemInfo.dims?.altura },
            query: itemInfo.query
          });
          return {
            action: 'mostrar_opcoes',
            data: { produtos: itemInfo.opcoes },
            response: `Encontrei ${itemInfo.opcoes.length} opções de ${itemInfo.query || 'produto'}:\n${opcoesTexto}\n\nDigite o número do produto (ex: 1, 2 ou 3)`,
            cliente: clienteMerged
          };
        }

        // Caso 1.5: erro de dimensão (largura excede largura_maxima do tubo)
        if (itemInfo.erro_dimensao) {
          setErroDimensao(itemInfo.erro_dimensao);
          const larg = itemInfo.erro_dimensao.largura_solicitada;
          const candidatos = itemInfo.erro_dimensao.candidatos_incompativeis || [];
          const tubosTxt = candidatos
            .slice(0, 4)
            .map(c => `${c.nome} (limite ${c.largura_maxima}m)`)
            .join('; ');
          return {
            action: 'erro_dimensao',
            response: `⚠️ A largura solicitada (${larg}m) excede o limite dos tubos mais comuns. Tubos disponíveis:\n${tubosTxt}\n\nTente um produto com tubo maior (ex: Tubo 38 ou 43) ou reduza a largura.`,
            cliente: clienteMerged
          };
        }
        // limpa erro_dimensao anterior quando há produto válido
        if (erroDimensao) setErroDimensao(null);

        // Caso 2: encontrou produto (1 único ou já validado)
        if (produto && produto.id) {
          const preco = parseFloat(produto.preco_venda) || 0;
          const dims = itemInfo.dims || {};
          const largura = dims.largura || 1;
          const altura = dims.altura || 1;
          const metodo = (produto.metodo_calculo || 'm2').toLowerCase();
          const area = largura * altura;
          const areaMinima = parseFloat(produto.area_minima) || 0;
          let subtotalProduto;
          if (metodo === 'ml' || metodo === 'linear') {
            subtotalProduto = largura * preco * qtd;
          } else {
            subtotalProduto = Math.max(area, areaMinima) * preco * qtd;
          }

          // Adiciona bandô se o RAG detectou
          const comBandoDetected = itemInfo.com_bando || comBandoLocal;
          const bandoValor = comBandoDetected ? calcBando(largura, bandoVenda) * qtd : 0;
          const subtotal = (itemInfo.subtotal || subtotalProduto) + bandoValor;

          const novoItem = {
            id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            tipo: 'produto',
            produto: {
              id: produto.id,
              nome: produto.nome,
              preco_venda: preco,
              codigo: produto.codigo,
              metodo_calculo: produto.metodo_calculo || 'm2'
            },
            selection: { largura, altura, quantidade: qtd },
            subtotal,
            com_bando: comBandoDetected,
            bando_valor: bandoValor,
            bando_venda_usado: comBandoLocal ? bandoVenda : null
          };

          setDraft(prev => ({
            ...prev,
            itens: [...prev.itens, novoItem],
            com_bando: false
          }));
          setUltimoProdutoConfirmar(null);
          setPendingSelection(null);

          const dimStr = (largura && altura) ? `\nDimensões: ${largura}m × ${altura}m` : '';
          const qtdStr = qtd > 1 ? `\nQuantidade: ${qtd}` : '';
          const bandoStr = comBandoDetected ? `\n✅ Bandô adicionado! (R$ ${fmt(bandoValor)})` : '';

          return {
            action: 'produto_adicionado',
            data: { item: novoItem },
            response: `✅ Adicionei ao orçamento:\n${produto.nome}${dimStr}${qtdStr}${bandoStr}\nSubtotal: R$ ${fmt(subtotal)}\n\nPosso adicionar mais alguma coisa?`,
            cliente: { ...clienteMerged, ...clienteUpdate }
          };
        }

        // Caso 3: não encontrou nada
        return {
          action: 'sem_produto',
          data: {},
          response: 'Não encontrei esse produto. Pode tentar com "cortina rolo blackout" ou "persiana screen 5%"?',
          cliente: clienteMerged
        };

      } catch (err) {
        console.warn('[OrcamentoChat] erro no RAG v2, fallback para busca local:', err.message);
        // Continua para buscar via Gemma como fallback
      }
    }

    return { action: null, data: {}, response: null, cliente: clienteMerged };
  }, [draft, supabase]);

  // ===========================================================================
  // EFEITOS COLATERAIS (useEffect)
  // ===========================================================================

  /**
   * Carrega lista de clientes ao iniciar
   * Usado para sugestao de clientes existentes
   */
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const lista = await clienteService.getAll();
        if (!cancelado && Array.isArray(lista)) {
          setClientes(lista);
        }
      } catch (err) {
        console.warn('[OrcamentoChat] falha ao carregar clientes:', err.message);
      }
      // Carrega bando_venda de configuracoes (preço por metro do bandô)
      try {
        const { supabase: sb } = await import('../supabase/client.js');
        const { data, error } = await sb
          .from('configuracoes')
          .select('bando_venda')
          .order('updated_at', { ascending: false })
          .limit(1);
        if (!cancelado && !error && data && data[0] && data[0].bando_venda != null) {
          setBandoVenda(parseFloat(data[0].bando_venda) || 120);
        }
      } catch (err) {
        console.warn('[OrcamentoChat] falha ao carregar bando_venda:', err.message);
      }
    })();
    return () => { cancelado = true; };
  }, []);

  /**
   * Scroll automatico para a ultima mensagem
   * Executa quando novas mensagens sao adicionadas
   */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ===========================================================================
  // CALCULOS DERIVADOS (useMemo)
  // ===========================================================================

  /**
   * Total dos itens do rascunho
   * Calculado em tempo real a cada mudanca nos itens
   */
  const totalDraft = useMemo(() => {
    return draft.itens.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  }, [draft.itens]);

  /**
   * Contagem de produtos e acessorios
   */
  const itemCounts = useMemo(() => {
    const produtos = draft.itens.filter(i => i.tipo !== 'acessorio').length;
    const acessorios = draft.itens.filter(i => i.tipo === 'acessorio').length;
    return { produtos, acessorios };
  }, [draft.itens]);

  // Soma das instalações por item (já incluídas no subtotal de cada item)
  const totalInstalacaoItens = useMemo(() => {
    return draft.itens.reduce((sum, i) => sum + (parseFloat(i.instalacao_valor) || 0), 0);
  }, [draft.itens]);

  // ===========================================================================
  // HANDLERS DE ACAO
  // ===========================================================================

  /**
   * Adiciona uma mensagem ao chat
   * Atualiza tanto o estado visual quanto o historico para API
   *
   * @param {Object} params
   * @param {string} params.role - 'user' | 'bot'
   * @param {string} params.content - Texto da mensagem
   * @param {boolean} params.isError - Se e uma mensagem de erro
   * @param {Object} params.toolResult - Resultado de tool call (opcional)
   */
  const addMessage = useCallback(({ role, content, isError = false, toolResult = null }) => {
    const newMessage = {
      role,
      content,
      timestamp: Date.now(),
      isError,
      toolResult,
    };

    setMessages(prev => [...prev, newMessage]);

    // Atualiza historico para API (apenas role e content)
    if (role !== 'system') {
      setChatHistory(prev => [...prev, { role, content }]);
    }

    return newMessage;
  }, []);

  /**
   * Atualiza um item especifico no rascunho
   * Usado quando o bot modifica dimensoes ou quantidade
   *
   * @param {string} itemId - ID do item a atualizar
   * @param {Object} updates - Campos a atualizar
   */
  const updateItemInDraft = useCallback((itemId, updates) => {
    setDraft(prev => ({
      ...prev,
      itens: prev.itens.map(item => {
        if (item.id !== itemId) return item;
        return { ...item, ...updates };
      })
    }));
  }, []);

  /**
   * Remove um item do rascunho
   *
   * @param {string} itemId - ID do item a remover
   */
  const removeItemFromDraft = useCallback((itemId) => {
    setDraft(prev => ({
      ...prev,
      itens: prev.itens.filter(item => item.id !== itemId)
    }));
    addMessage({
      role: 'bot',
      content: 'Item removido do orçamento.'
    });
  }, [addMessage]);

  /**
   * Executa um tool call recebido do bot
   * Gerencia as acoes: buscar_produto, adicionar_produto, finalizar, etc
   *
   * @param {Object} toolCall - Tool call da API {name, arguments}
   * @returns {Promise<Object>} Resultado do tool call
   */
  const executeToolCall = useCallback(async (toolCall) => {
    const { name, arguments: args } = toolCall;
    const parsedArgs = typeof args === 'string' ? JSON.parse(args) : args;

    switch (name) {
      case 'buscar_produto': {
        // Se o servidor Node já executou a tool (vem com .result populado),
        // usa esse resultado para adicionar automaticamente o produto escolhido.
        const serverResult = toolCall.result;
        if (serverResult?.resultados?.length > 0) {
          const escolhido = serverResult.resultados.find(r => r.escolhido);
          if (escolhido && !serverResult.precisa_escolha) {
            // Produto já foi resolvido pelo RAG v2 → adiciona direto ao draft
            // Pega a última mensagem do usuário do histórico
            const lastUserMsg = [...chatHistory].reverse().find(m => m.role === 'user')?.content || parsedArgs.query || '';
            const dimsFromMsg = extractDimensions(lastUserMsg);
            // Prefere dims do backend (mais confiável) — cai pro regex só se faltar
            const largura = serverResult.dims?.largura ?? dimsFromMsg.largura ?? 1;
            const altura = serverResult.dims?.altura ?? dimsFromMsg.altura ?? 1;
            const preco = parseFloat(escolhido.preco_venda) || 0;
            const area = largura * altura;
            const metodo = (escolhido.metodo_calculo || 'm2').toLowerCase();
            const areaMinima = parseFloat(escolhido.area_minima) || 0;
            const subtotalProduto = metodo === 'ml' || metodo === 'linear'
              ? largura * preco
              : Math.max(area, areaMinima) * preco;
            const comBandoTool = /band[ôo]/i.test(lastUserMsg);
            const flagBando = prev?.com_bando || comBandoTool;
            const bandoValor = flagBando ? calcBando(largura, bandoVenda) : 0;
            const subtotal = (serverResult.subtotal ?? subtotalProduto) + bandoValor;
            const novoItem = {
              id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              tipo: 'produto',
              produto: {
                id: escolhido.id,
                nome: escolhido.nome,
                preco_venda: preco,
                codigo: escolhido.codigo,
                metodo_calculo: escolhido.metodo_calculo || 'm2',
              },
              selection: { largura, altura, quantidade: 1 },
              subtotal,
              com_bando: flagBando,
              bando_valor: bandoValor,
              bando_venda_usado: flagBando ? bandoVenda : null
            };
            setDraft(prev => ({
              ...prev,
              itens: [...prev.itens, novoItem],
              com_bando: false,
            }));
            return {
              title: 'Produto Adicionado',
              content: `✅ Adicionei: ${escolhido.nome}\nDimensões: ${largura}m × ${altura}m\nSubtotal: R$ ${fmt(subtotal)}${flagBando ? `\n✅ Bandô adicionado! (R$ ${fmt(bandoValor)})` : ''}\n\nMais alguma coisa?`,
              products: serverResult.resultados,
            };
          }
          // Se precisa_escolha=true ou não tem escolhido, mostra lista
          return {
            title: 'Opções Encontradas',
            content: `Encontrei ${serverResult.resultados.length} opção(ões):`,
            products: serverResult.resultados,
          };
        }

        // Fallback: chama /api/rag-search (caminho antigo)
        const { query } = parsedArgs;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;

          const response = await fetch(`${API_BASE_URL}/rag-search`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              query,
              limit: 5,
              table: 'produtos'
            })
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const data = await response.json();

          return {
            title: 'Busca de Produtos',
            content: data.produtos?.length > 0
              ? `Encontrei ${data.produtos.length} produto(s) para "${query}"`
              : `Nenhum produto encontrado para "${query}". Tente outro termo.`,
            products: data.produtos || []
          };
        } catch (err) {
          console.error('[OrcamentoChat] erro na busca RAG:', err);
          return {
            title: 'Erro na Busca',
            content: 'Não foi possível buscar produtos. Tente novamente.',
            products: []
          };
        }
      }

      case 'adicionar_produto': {
        // Adiciona produto ao rascunho
        const { produto_id, nome, largura, altura, quantidade, preco_unitario, metodo_calculo } = parsedArgs;

        const novoItem = {
          id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          tipo: 'produto',
          produto: {
            id: produto_id,
            nome,
            preco_venda: preco_unitario,
            metodo_calculo: metodo_calculo || 'm2'
          },
          selection: {
            largura: parseFloat(largura),
            altura: parseFloat(altura),
            quantidade: parseInt(quantidade) || 1
          },
          subtotal: parseFloat(preco_unitario || 0) * parseFloat(largura) * parseFloat(altura) * (parseInt(quantidade) || 1)
        };

        const flagBandoItem = prev?.com_bando;
        const bandoValorItem = flagBandoItem
          ? calcBando(parseFloat(largura), bandoVenda) * (parseInt(quantidade) || 1)
          : 0;
        novoItem.subtotal += bandoValorItem;
        novoItem.com_bando = !!flagBandoItem;
        novoItem.bando_valor = bandoValorItem;
        novoItem.bando_venda_usado = flagBandoItem ? bandoVenda : null;

        setDraft(prev => ({
          ...prev,
          itens: [...prev.itens, novoItem],
          com_bando: false
        }));

        setStep('produto');

        return {
          title: 'Produto Adicionado',
          content: `${nome} adicionado ao orçamento!\nDimensões: ${largura}m × ${altura}m\nQuantidade: ${quantidade}\nSubtotal: R$ ${fmt(novoItem.subtotal)}`
        };
      }

      case 'adicionar_acessorio': {
        // Adiciona acessorio ao rascunho
        const { acessorio_id, nome, quantidade, preco_unitario, unidade } = parsedArgs;

        const novoItem = {
          id: `acc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          tipo: 'acessorio',
          produto: {
            id: acessorio_id,
            nome
          },
          quantity: parseInt(quantidade) || 1,
          unit_price: parseFloat(preco_unitario) || 0,
          unit: unidade || 'un',
          subtotal: (parseFloat(preco_unitario) || 0) * (parseInt(quantidade) || 1)
        };

        setDraft(prev => ({
          ...prev,
          itens: [...prev.itens, { ...novoItem, com_bando: prev.com_bando }],
          com_bando: false
        }));

        return {
          title: 'Acessório Adicionado',
          content: `${nome} adicionado!\nQuantidade: ${quantidade}\nSubtotal: R$ ${fmt(novoItem.subtotal)}`
        };
      }

      case 'atualizar_cliente': {
        // Atualiza info do cliente no rascunho
        const { nome, telefone, email, endereco } = parsedArgs;

        setDraft(prev => ({
          ...prev,
          cliente: {
            ...prev.cliente,
            nome: nome || prev.cliente.nome,
            telefone: telefone || prev.cliente.telefone,
            email: email || prev.cliente.email,
            endereco: endereco || prev.cliente.endereco
          }
        }));

        setStep('cliente');

        return {
          title: 'Cliente Atualizado',
          content: nome ? `Dados do cliente "${nome}" salvos!` : 'Dados do cliente atualizados.'
        };
      }

      case 'remover_item': {
        // Remove item pelo ID
        const { item_id } = parsedArgs;
        const item = draft.itens.find(i => i.id === item_id);

        if (item) {
          removeItemFromDraft(item_id);
          return {
            title: 'Item Removido',
            content: `"${item.produto?.nome || item.nome}" removido do orçamento.`
          };
        }

        return {
          title: 'Aviso',
          content: 'Item não encontrado no orçamento.'
        };
      }

      case 'listar_itens': {
        // Lista todos os itens atuais
        if (draft.itens.length === 0) {
          return {
            title: 'Orçamento Vazio',
            content: 'Nenhum item adicionado ainda ao orçamento.'
          };
        }

        const listaTexto = draft.itens.map((item, idx) => {
          const nome = item.produto?.nome || item.nome || 'Item';
          const tipo = item.tipo === 'acessorio' ? '[Acessório]' : '';
          return `${idx + 1}. ${tipo} ${nome}\n   Qtd: ${item.tipo === 'acessorio' ? item.quantity : item.selection?.quantidade}\n   Subtotal: R$ ${fmt(item.subtotal)}`;
        }).join('\n\n');

        return {
          title: 'Resumo do Orçamento',
          content: `Itens (${draft.itens.length}):\n\n${listaTexto}\n\nTotal: R$ ${fmt(totalDraft)}`
        };
      }

      case 'finalizar_orcamento': {
        // Inicia o processo de salvamento com dados atuais do draft
        return await handleSalvarOrcamento({
          cliente: draft.cliente,
          itens: draft.itens,
          total: totalDraft
        });
      }

      default:
        return {
          title: 'Ação Desconhecida',
          content: `Não sei como executar: ${name}`
        };
    }
  }, [draft.itens, removeItemFromDraft, totalDraft, chatHistory, extractDimensions]);

  /**
   * Salva o orçamento no banco de dados
   * Chamado quando o bot executa finalizar_orcamento
   *
   * Fluxo:
   * 1. Valida cliente e itens
   * 2. Busca ou cria cliente no banco
   * 3. Insere orçamento na tabela orcamentos
   * 4. Atualiza cache local
   * 5. Navega para visualizacao
   * @param {Object} overrideData - Dados override para o draft (para evitar race condition com setState)
   */
  const handleSalvarOrcamento = useCallback(async (overrideData) => {
    // Usa dados override se fornecidos (para evitar race condition), senão usa estado
    const clienteData = overrideData?.cliente || draft.cliente;
    const itensData = overrideData?.itens || draft.itens;
    const totalData = overrideData?.total || totalDraft;
    // Instalação agora é por-item (item.instalacao_valor já está somado no subtotal de cada item)
    const descontoPix = paymentMethod === 'pix' ? totalDraft * discountRate / 100 : 0;
    const totalFinal = totalDraft - descontoPix;

    // Validacoes basicas
    if (!clienteData.nome) {
      return {
        title: 'Atenção',
        content: 'Preciso do nome do cliente para finalizar. Qual o nome do cliente?'
      };
    }

    if (itensData.length === 0) {
      return {
        title: 'Atenção',
        content: 'Adicione pelo menos um produto ao orçamento antes de finalizar.'
      };
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // 1) Encontrar ou criar cliente
      let clienteId = null;

      // Procura cliente existente pelo nome
      const clienteExistente = clientes.find(
        c => c.name?.toLowerCase().trim() === clienteData.nome.toLowerCase().trim()
      );

      if (clienteExistente) {
        clienteId = clienteExistente.id;
      } else {
        // Cria novo cliente
        const novoCliente = await clienteService.create({
          name: clienteData.nome.toUpperCase(),
          phone: clienteData.telefone || null,
          email: clienteData.email || null,
          address: clienteData.endereco?.toUpperCase() || null,
        });

        if (!novoCliente?.id) {
          throw new Error('Falha ao criar cliente');
        }

        clienteId = novoCliente.id;
        setClientes(prev => [...prev, novoCliente]);
      }

      // 2) Preparar dados dos produtos
      const cleanProducts = itensData
        .filter(i => i.tipo !== 'acessorio')
        .map(i => ({
          produto_id: i.produto.id,
          produto: {
            id: i.produto.id,
            nome: i.produto.nome,
            codigo: i.produto.codigo,
            metodo_calculo: i.produto.metodo_calculo
          },
          largura: parseFloat(i.selection.largura),
          altura: parseFloat(i.selection.altura),
          input_width: parseFloat(i.selection.largura),
          input_height: parseFloat(i.selection.altura),
          quantidade: parseInt(i.selection.quantidade) || 1,
          ambiente: i.selection.ambiente || '',
          modelo: i.selection.modelo || '',
          acionamento: i.selection.acionamento || '',
          cor: i.selection.cor || '',
          customizacao: i.customizacao || {},
          subtotal: i.subtotal
        }));

      // 3) Preparar dados dos acessorios
      const cleanAccessories = itensData
        .filter(i => i.tipo === 'acessorio')
        .map(i => ({
          accessory_id: i.produto.id,
          name: i.produto.nome,
          unit: i.unit || '',
          color: i.color || '',
          unit_price: Number(i.unit_price) || 0,
          quantity: Number(i.quantity) || 1,
          subtotal: Number(i.subtotal) || 0,
          valor_total: Number(i.subtotal) || 0
        }));

      // 4) Inserir orçamento
      const { data: orcamento, error } = await supabase
        .from('orcamentos')
        .insert([{
          cliente_id: clienteId,
          vendedor_id: user?.id || null,
          valor_total: totalFinal,
          valor_produtos: totalData,
          discount_amount: descontoPix,
          cliente_nome: clienteData.nome,
          cliente_telefone: clienteData.telefone || null,
          cliente_endereco: clienteData.endereco || null,
          instalacao_valor: totalInstalacaoItens,
          payment_method: paymentMethod,
          installments: parseInt(installments) || 1,
          discount_rate: parseFloat(discountRate) || 0,
          produtos_json: JSON.stringify(cleanProducts),
          acessorios_json: JSON.stringify(cleanAccessories),
          ambientes: JSON.stringify([]),
          observacao: '',
          status: 'pendente'
        }])
        .select()
        .single();

      if (error) throw error;

      // 5) Atualizar cache local
      try {
        const { localDB } = await import('../services/localDatabase');
        await localDB.put('orcamentos', orcamento);
      } catch (e) {
        console.warn('[OrcamentoChat] falha ao salvar no cache local:', e?.message);
      }

      setStep('finalizado');

      return {
        title: 'Orçamento Salvo!',
        content: `Orçamento #${orcamento.id?.slice(0, 8)} criado com sucesso!\n\nCliente: ${clienteData.nome}\nItens: ${itensData.length}\nTotal: R$ ${fmt(totalData)}\n\nRedirecionando para visualização...`,
        savedOrcamentoId: orcamento.id
      };

    } catch (err) {
      console.error('[OrcamentoChat] erro ao salvar:', err);
      return {
        title: 'Erro',
        content: `Não foi possível salvar o orçamento: ${err.message}`
      };
    } finally {
      setLoading(false);
    }
  }, [draft, clientes]);

  /**
   * Envia mensagem para a API de chat
   * Usa intent parser para detectar ações localmente (rápido)
   * e só envia para Gemma quando necessário
   *
   * @param {string} userMessage - Mensagem do usuario
   */
  const sendToChatAPI = useCallback(async (userMessage) => {
    setLoading(true);
    setIsTyping(true);

    try {
      // 1) Adiciona mensagem do usuario ao chat
      addMessage({ role: 'user', content: userMessage });

      // 2) PROCESSAMENTO LOCAL PRIMEIRO
      // Detecta intenções comuns sem precisar chamar a API
      const intent = await processLocalIntent(userMessage);

      // AÇÃO: NOVO ORÇAMENTO
      if (intent.action === 'novo') {
        setDraft({
          cliente: { nome: '', telefone: '', email: '', endereco: '' },
          itens: [],
          com_bando: false
        });
        addMessage({
          role: 'bot',
          content: 'Ótimo! Orçamento limpo. Como posso ajudar? 😊'
        });
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // AÇÃO: LISTAR ITENS
      if (intent.action === 'listar') {
        if (draft.itens.length === 0) {
          addMessage({
            role: 'bot',
            content: 'Seu orçamento está vazio. Adicione produtos para eu listar!'
          });
        } else {
          const lista = draft.itens.map((item, idx) => {
            const nome = item.produto?.nome || 'Item';
            const dims = item.selection ? `${item.selection.largura}m × ${item.selection.altura}m` : '';
            const qtd = item.tipo === 'acessorio' ? item.quantity : item.selection?.quantidade;
            return `${idx + 1}. ${nome}\n   ${dims} | Qtd: ${qtd} | R$ ${fmt(item.subtotal)}`;
          }).join('\n\n');
          addMessage({
            role: 'bot',
            content: `📋 **Itens do Orçamento:**\n\n${lista}\n\n**Total: R$ ${fmt(totalDraft)}**`
          });
        }
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // AÇÃO: FINALIZAR
      if (intent.action === 'finalizar') {
        // Executa salvamento DIRETAMENTE sem depender da resposta do Gemma
        if (intent.data.error === 'precisa_cliente') {
          addMessage({
            role: 'bot',
            content: 'Antes de finalizar, preciso saber o nome do cliente. Qual o nome?'
          });
          setLoading(false);
          setIsTyping(false);
          return;
        }
        if (intent.data.error === 'precisa_itens') {
          addMessage({
            role: 'bot',
            content: 'Adicione pelo menos um produto antes de finalizar!'
          });
          setLoading(false);
          setIsTyping(false);
          return;
        }
        // Executa finalização - PASSANDO dados do intent para evitar race condition
        // intent.cliente contém o cliente mesclado (com dados extraídos da mensagem)
        const result = await handleSalvarOrcamento({
          cliente: intent.cliente,
          itens: draft.itens,
          total: totalDraft
        });
        addMessage({
          role: 'bot',
          content: result.content,
          toolResult: result
        });
        if (result.savedOrcamentoId) {
          setTimeout(() => {
            navigate(`/budgets/${result.savedOrcamentoId}/view`);
          }, 2000);
        }
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // AÇÃO: PRODUTO JÁ ADICIONADO (intent local)
      if (intent.action === 'produto_adicionado') {
        addMessage({ role: 'bot', content: intent.response });
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // AÇÃO: CONFIRMAR PRODUTO
      if (intent.action === 'confirmar_produto') {
        addMessage({ role: 'bot', content: intent.response });
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // AÇÃO: BANDÔ DETECTADO
      if (intent.action === 'bando_detectado') {
        addMessage({ role: 'bot', content: intent.response });
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // AÇÃO: RESPOSTA DIRETA DO INTENT
      if (intent.response) {
        addMessage({ role: 'bot', content: intent.response });
        setLoading(false);
        setIsTyping(false);
        return;
      }

      // 3) NENHUM INTENT LOCAL - ENVIA PARA GEMMA
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      // Usa dados do cliente extraídos pelo intent (ou os antigos)
      const clienteInfo = intent.cliente || draft.cliente;

      // Contexto do orçamento enviado como system message separada
      // (NÃO concatenar com user message — isso polui a query que o Ollama
      // passa para buscar_produto, e o RAG v2 recebe lixo)
      const contextInfo = draft.itens.length > 0
        ? `[ORÇAMENTO ATUAL] Cliente: ${clienteInfo.nome || '(não informado)'}${clienteInfo.telefone ? ` | Tel: ${clienteInfo.telefone}` : ''}. Itens: ${draft.itens.map((i, idx) => `${idx + 1}. ${i.produto?.nome} | ${i.selection?.largura || 1}m x ${i.selection?.altura || 1}m | R$ ${fmt(i.subtotal)}`).join(' | ')}. Total: R$ ${fmt(totalDraft)}`
        : (clienteInfo.nome ? `[ORÇAMENTO ATUAL] Cliente: ${clienteInfo.nome}${clienteInfo.telefone ? ` | Tel: ${clienteInfo.telefone}` : ''}. (Itens: nenhum ainda)` : '');

      const messages = [...chatHistory, { role: 'user', content: userMessage }];
      // Se tiver contexto, adiciona como system message EXTRA (não concatena)
      if (contextInfo) {
        messages.unshift({ role: 'system', content: contextInfo });
      }

      const payload = {
        messages,
        draft: {
          cliente: clienteInfo,
          itens: draft.itens,
          total: totalDraft
        }
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);

      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      // 4) Processa resposta do Gemma
      const data = await response.json();

      // Tool calls (para funcionalidades futuras)
      if (data.tool_calls && data.tool_calls.length > 0) {
        for (const toolCall of data.tool_calls) {
          const result = await executeToolCall(toolCall);
          addMessage({
            role: 'bot',
            content: result.content,
            toolResult: result
          });
          if (result.savedOrcamentoId) {
            setTimeout(() => {
              navigate(`/budgets/${result.savedOrcamentoId}/view`);
            }, 2000);
          }
        }
      }

      // Resposta de texto
      if (data.reply) {
        addMessage({ role: 'bot', content: data.reply });
      }

    } catch (err) {
      console.error('[OrcamentoChat] erro na API:', err);
      addMessage({
        role: 'bot',
        content: 'Desculpe, tive um problema ao processar sua mensagem. Tente novamente.',
        isError: true
      });
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  }, [addMessage, chatHistory, draft, totalDraft, executeToolCall, navigate, processLocalIntent, supabase]);

  /**
   * Handler para envio de mensagem
   * Intercepta Enter (envia) e Shift+Enter (nova linha)
   *
   * @param {Object} e - Evento do textarea
   */
  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const message = inputValue.trim();
      if (message) {
        sendToChatAPI(message);
        setInputValue('');
      }
    }
  }, [inputValue, sendToChatAPI]);

  /**
   * Handler para botoes de acao rapida
   *
   * @param {string} action - Acao pre-definida
   */
  const handleQuickAction = useCallback((action) => {
    const actions = {
      'iniciar': 'Quero iniciar um novo orçamento.',
      'cancelar': 'Cancelar este orçamento e começar um novo.',
      'listar': 'Liste os itens do meu orçamento atual.',
      'finalizar': 'Finalize o orçamento.'
    };

    if (actions[action]) {
      sendToChatAPI(actions[action]);
    }
  }, [sendToChatAPI]);

  /**
   * Reseta o chat para comecar um novo orçamento
   */
  const handleNovoOrcamento = useCallback(() => {
    setDraft({
      cliente: { nome: '', telefone: '', email: '', endereco: '' },
      itens: [],
      com_bando: false
    });
    setStep('inicio');
    setMessages([
      {
        role: 'bot',
        content: MENSAGEM_BOAS_VINDAS,
        timestamp: Date.now()
      }
    ]);
    setChatHistory([chatHistory[0]]); // Mantem system prompt
  }, [chatHistory]);

  // ===========================================================================
  // RENDERIZACAO
  // ===========================================================================

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      background: '#f3f4f6',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      overflow: 'hidden'
    }}>
      {/* ===================================================================== */}
      {/* AREA PRINCIPAL DO CHAT */}
      {/* ===================================================================== */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: showDraftPanel ? 'calc(100% - 380px)' : '100%',
        transition: 'max-width 0.3s ease',
        minWidth: 0
      }}>
        {/* Header */}
        <header style={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
              Chat de Orçamento
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
              Crie orçamentos com ajuda da IA
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowDraftPanel(prev => !prev)}
              style={{
                padding: '8px 12px',
                background: showDraftPanel ? '#e5e7eb' : '#2563eb',
                color: showDraftPanel ? '#374151' : 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500
              }}
            >
              {showDraftPanel ? 'Ocultar Resumo' : 'Ver Resumo'}
            </button>

            <button
              onClick={handleNovoOrcamento}
              style={{
                padding: '8px 12px',
                background: '#fef3c7',
                color: '#92400e',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Novo Orçamento
            </button>
          </div>
        </header>

        {/* Area de mensagens */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {messages.map((msg, idx) => (
            <MessageBubble
              key={idx}
              message={msg}
              onEditItem={(itemId, updates) => updateItemInDraft(itemId, updates)}
              onDeleteItem={(itemId) => removeItemFromDraft(itemId)}
            />
          ))}

          {/* Indicador de digitacao */}
          {isTyping && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0'
            }}>
              <div style={{
                display: 'flex',
                gap: 4,
                padding: '12px 16px',
                background: '#f3f4f6',
                borderRadius: 16,
                borderBottomLeftRadius: 4
              }}>
                <span style={{
                  width: 8,
                  height: 8,
                  background: '#6b7280',
                  borderRadius: '50%',
                  animation: 'bounce 1.4s infinite ease-in-out',
                  animationDelay: '0s'
                }} />
                <span style={{
                  width: 8,
                  height: 8,
                  background: '#6b7280',
                  borderRadius: '50%',
                  animation: 'bounce 1.4s infinite ease-in-out',
                  animationDelay: '0.2s'
                }} />
                <span style={{
                  width: 8,
                  height: 8,
                  background: '#6b7280',
                  borderRadius: '50%',
                  animation: 'bounce 1.4s infinite ease-in-out',
                  animationDelay: '0.4s'
                }} />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Botoes de acao rapida */}
        {step === 'inicio' && (
          <div style={{
            padding: '0 20px 12px',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => handleQuickAction('iniciar')}
              style={{
                padding: '10px 16px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 20,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Iniciar Orçamento
            </button>
            <button
              onClick={() => handleQuickAction('listar')}
              style={{
                padding: '10px 16px',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 20,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500
              }}
            >
              Ver Itens Atuais
            </button>
          </div>
        )}

        {/* Input de mensagem */}
        <div style={{
          padding: '12px 20px',
          background: 'white',
          borderTop: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-end'
          }}>
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Digite sua mensagem... (Enter para enviar, Shift+Enter para nova linha)"
              disabled={loading}
              rows={2}
              style={{
                flex: 1,
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: 12,
                fontSize: 14,
                resize: 'none',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'border-color 0.2s',
                ':focus': {
                  borderColor: '#2563eb'
                }
              }}
              onFocus={(e) => e.target.style.borderColor = '#2563eb'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />

            <button
              onClick={() => {
                const msg = inputValue.trim();
                if (msg) {
                  sendToChatAPI(msg);
                  setInputValue('');
                }
              }}
              disabled={loading || !inputValue.trim()}
              style={{
                padding: '10px 20px',
                background: inputValue.trim() ? '#2563eb' : '#e5e7eb',
                color: inputValue.trim() ? 'white' : '#9ca3af',
                border: 'none',
                borderRadius: 10,
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                fontSize: 14,
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
            >
              Enviar
            </button>
          </div>

          <p style={{
            margin: '8px 0 0',
            fontSize: 11,
            color: '#9ca3af',
            textAlign: 'center'
          }}>
            Pressione Enter para enviar, Shift+Enter para nova linha
          </p>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* PAINEL DE RESUMO DO ORCAMENTO */}
      {/* ===================================================================== */}
      {showDraftPanel && (
        <aside style={{
          width: 360,
          background: 'white',
          borderLeft: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header do painel */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
              Resumo do Orçamento
            </h2>
          </div>

          {/* Info do Cliente */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <h3 style={{
              margin: '0 0 8px',
              fontSize: 12,
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase'
            }}>
              Cliente
            </h3>

            {/* Select para escolher cliente já cadastrado */}
            {clientes.length > 0 && (
              <select
                value={draft.cliente._existingId || ''}
                onChange={(e) => {
                  const cid = e.target.value;
                  if (!cid) return;
                  const c = clientes.find(x => x.id === cid);
                  if (!c) return;
                  setDraft(prev => ({
                    ...prev,
                    cliente: {
                      ...prev.cliente,
                      _existingId: cid,
                      nome: c.name || '',
                      telefone: c.phone || '',
                      email: c.email || '',
                      endereco: c.address || ''
                    }
                  }));
                }}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 13,
                  marginBottom: 8,
                  background: 'white'
                }}
              >
                <option value="">— Selecionar cliente cadastrado —</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.phone ? ` · ${c.phone}` : ''}
                  </option>
                ))}
              </select>
            )}

            {draft.cliente.nome ? (
              <div>
                <div style={{ fontWeight: 600, color: '#111827' }}>
                  {draft.cliente.nome}
                </div>
                {draft.cliente.telefone && (
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                    {draft.cliente.telefone}
                  </div>
                )}
                {draft.cliente.email && (
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                    {draft.cliente.email}
                  </div>
                )}
                {draft.cliente.endereco && (
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                    {draft.cliente.endereco}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                fontSize: 13,
                color: '#9ca3af',
                fontStyle: 'italic'
              }}>
                Cliente não informado ainda
              </div>
            )}
          </div>

          {/* Lista de Itens */}
          <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
            <h3 style={{
              margin: '0 0 8px',
              fontSize: 12,
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase'
            }}>
              Itens ({draft.itens.length})
            </h3>

            {draft.itens.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '24px 0',
                color: '#9ca3af',
                fontSize: 13
              }}>
                Nenhum item adicionado
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {draft.itens.map(item => (
                  <ProductItemCard
                    key={item.id}
                    item={item}
                    onEdit={(itemId, updates) => updateItemInDraft(itemId, updates)}
                    onDelete={(itemId) => removeItemFromDraft(itemId)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer com Total e Acoes */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            background: '#f9fafb',
            maxHeight: '50vh',
            overflowY: 'auto'
          }}>
            {/* Instalação (agora por item, adicionado direto no card) */}
            <div style={{ marginBottom: 12, padding: 10, background: '#eff6ff', border: '1px dashed #93c5fd', borderRadius: 6 }}>
              <label style={{ fontSize: 11, color: '#1e40af', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                💡 Instalação agora é por item
              </label>
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                Adicione a instalação direto no card de cada produto, clicando em "+ Adicionar instalação".
              </div>
            </div>

            {/* Forma de pagamento */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>
                Forma de pagamento
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 13,
                  marginTop: 4,
                  background: 'white'
                }}
              >
                <option value="pix">PIX (com desconto)</option>
                <option value="cartao">Cartão de Crédito</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="boleto">Boleto</option>
              </select>
            </div>

            {/* Parcelas (cartao) ou desconto (pix) */}
            {paymentMethod === 'cartao' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>
                  Parcelas no cartão
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={installments}
                  onChange={(e) => setInstallments(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 13,
                    marginTop: 4,
                    background: 'white'
                  }}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  {installments}x de R$ {fmt((totalDraft + (parseFloat(instalacaoValor) || 0)) / Math.max(1, installments))}
                </div>
              </div>
            )}

            {paymentMethod === 'pix' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>
                  Desconto no PIX (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="20"
                  step="0.5"
                  value={discountRate}
                  onChange={(e) => setDiscountRate(Math.max(0, Math.min(20, parseFloat(e.target.value) || 0)))}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 13,
                    marginTop: 4,
                    background: 'white'
                  }}
                />
                {discountRate > 0 && (
                  <div style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>
                    Desconto: −R$ {fmt(totalDraft * discountRate / 100)}
                  </div>
                )}
              </div>
            )}

            {/* Totais */}
            <div style={{ marginBottom: 12 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#6b7280',
                marginBottom: 4
              }}>
                <span>Produtos:</span>
                <span>{itemCounts.produtos}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#6b7280',
                marginBottom: 4
              }}>
                <span>Acessórios:</span>
                <span>{itemCounts.acessorios}</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#6b7280',
                marginBottom: 4
              }}>
                <span>Subtotal:</span>
                <span>R$ {fmt(totalDraft)}</span>
              </div>
              {totalInstalacaoItens > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#6b7280',
                  marginBottom: 4
                }}>
                  <span>Instalação:</span>
                  <span>R$ {fmt(totalInstalacaoItens)}</span>
                </div>
              )}
              {paymentMethod === 'pix' && discountRate > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#059669',
                  marginBottom: 4
                }}>
                  <span>Desconto PIX ({discountRate}%):</span>
                  <span>−R$ {fmt(totalDraft * discountRate / 100)}</span>
                </div>
              )}
              {paymentMethod === 'cartao' && installments > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  color: '#6b7280',
                  marginBottom: 4
                }}>
                  <span>{installments}x no cartão:</span>
                  <span>R$ {fmt(totalDraft / installments)}/mês</span>
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 18,
                fontWeight: 700,
                color: '#059669',
                borderTop: '1px solid #e5e7eb',
                paddingTop: 6,
                marginTop: 6
              }}>
                <span>Total:</span>
                <span>R$ {fmt(totalDraft + instalacaoValor - (paymentMethod === 'pix' ? totalDraft * discountRate / 100 : 0))}</span>
              </div>
            </div>

            {/* Botoes de acao */}
            <button
              onClick={() => handleQuickAction('listar')}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 8
              }}
            >
              Revisar com Assistente
            </button>

            <button
              onClick={() => handleQuickAction('finalizar')}
              disabled={step === 'finalizado'}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: step === 'finalizado' ? '#e5e7eb' : '#059669',
                color: step === 'finalizado' ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: 8,
                cursor: step === 'finalizado' ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 700
              }}
            >
              {step === 'finalizado' ? 'Orçamento Salvo!' : 'Finalizar Orçamento'}
            </button>
          </div>
        </aside>
      )}

      {/* ===================================================================== */}
      {/* ESTILOS CSS INLINE (ANIMACOES) */}
      {/* ===================================================================== */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }

        /* Scrollbar personalizada */
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: #f3f4f6;
        }
        ::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
}

export default OrcamentoChat;
